import AWS from "aws-sdk";
import { LabelingJobSummary, SageMaker } from "@aws-sdk/client-sagemaker";
import {
  Campaign,
  CampaignLabelingJob,
  CampaignPrefilteringJob,
  CampaignPrefilteringEndpoint,
  CampaignManifest,
  ManifestType,
} from "../appstore/models";
import appConfig from "./appConfig";

const CAMPAIGN_TABLE = "geo-gt-campaign";
const CAMPAIGN_PREFILTERING_TABLE = "geo-gt-campaign-prefiltering";
const CAMPAIGN_MANIFEST_TABLE = "geo-gt-campaign-manifest";
const CAMPAIGN_LABELING_JOB_TABLE = "geo-gt-campaign-labeling-job";

const CAMPAIGN_PREFILTERING_ENDPOINT_TABLE =
  "geo-gt-campaign-prefiltering-endpoint";
export type CreateCampaignRequest = {
  job_name: string;
  tile_size?: number;
  input_geotiffs_s3_prefix: string;
  output_campaign_s3_prefix: string;
  sm_detect_features_endpoint?: string;
};
export type CreateCampaignResult =
  | {
      success: true;
      step_function_execution_arn: string;
    }
  | {
      success: false;
      errors: string[];
      errorMessage?: string;
    };
export const RESERVED_CAMPAIGN_NAMES = ["new"];

export type CreateCampaignPrefilteringJobRequest = {
  job_name: string;
  sm_detect_features_endpoint: string;
};
export type CreateCampaignPrefilteringJobResult =
  | {
      success: true;
      CampaignPrefilteringId: string;
    }
  | {
      success: false;
      errors: string[];
      errorMessage?: string;
    };

export type CreateCampaignLabelingJobRequest = {
  job_name: string;
  sm_detect_features_endpoint: string;
};
export type CreateCampaignLabelingJobResult =
  | {
      success: true;
      CampaignLabelingJobId: string;
    }
  | {
      success: false;
      errors: string[];
      errorMessage?: string;
    };

export type LinkCampaignLabelingJobResult =
  | {
      success: true;
    }
  | {
      success: false;
      errors: string[];
      errorMessage?: string;
    };

export type CreateCampaignManifestResult =
  | {
      success: true;
    }
  | {
      success: false;
      errors: string[];
      errorMessage?: string;
    };
export type GenerateCampaignManifestResult =
  | {
      success: true;
    }
  | {
      success: false;
      errors: string[];
      errorMessage?: string;
    };
export async function describeCampaign(
  jobName: string
): Promise<Campaign | undefined> {
  const dynamodb = new AWS.DynamoDB({
    endpoint: `dynamodb.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  const campaign = await new Promise<Campaign | undefined>(
    (resolve, reject) => {
      dynamodb.getItem(
        {
          Key: AWS.DynamoDB.Converter.marshall({
            JobName: jobName,
          }),
          TableName: CAMPAIGN_TABLE,
        },
        (err, data) => {
          if (err) {
            reject(`Error fetching data: ${err.code}`);
            return;
          }
          if (!data.Item) {
            resolve(undefined);
            return;
          }
          const campaign = AWS.DynamoDB.Converter.unmarshall(
            data.Item
          ) as unknown as Campaign;
          resolve(campaign);
        }
      );
    }
  );
  return campaign;
}

export async function listCampaigns(
  pageSize: number,
  LastEvaluatedKey?: AWS.DynamoDB.Key
): Promise<{
  campaigns: Campaign[];
  LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
  count: number;
}> {
  const dynamodb = new AWS.DynamoDB({
    endpoint: `dynamodb.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  return await new Promise<{
    campaigns: Campaign[];
    LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
    count: number;
  }>((resolve, reject) => {
    dynamodb.query(
      {
        ExclusiveStartKey: LastEvaluatedKey,
        TableName: CAMPAIGN_TABLE,
        Limit: pageSize,
        ExpressionAttributeValues: {
          ":v1": {
            S: `${appConfig.awsAccountId}-${appConfig.awsRegion}`,
          },
        },
        IndexName: "geo-gt-campaign-account-id-region-to-date-created-index",
        KeyConditionExpression: "AccountIdRegion = :v1",
        ScanIndexForward: false,
      },
      (err, data) => {
        if (err) {
          reject(`Error fetching data: ${err.code}`);
          return;
        }
        if (!data.Items) {
          resolve({
            campaigns: [],
            LastEvaluatedKey: undefined,
            count: 0,
          });
          return;
        }

        const campaigns = data.Items.map(
          (item) =>
            AWS.DynamoDB.Converter.unmarshall(item) as unknown as Campaign
        );
        resolve({
          campaigns,
          LastEvaluatedKey: data.LastEvaluatedKey,
          count: data.Count || 0,
        });
      }
    );
  });
}

export async function createCampaign(
  request: CreateCampaignRequest
): Promise<CreateCampaignResult> {
  const lambda = new AWS.Lambda({
    endpoint: `lambda.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  const result = await lambda
    .invoke({
      FunctionName: "initialize_campaign_metadata",
      Payload: JSON.stringify(request),
    })
    .promise();
  if (!result.Payload) {
    return {
      success: false,
      errors: ["No payload returned from lambda initialize_campaign_metadata"],
    };
  }
  const createCampaignResult = JSON.parse(
    result.Payload.toString()
  ) as CreateCampaignResult;
  if ("errorMessage" in createCampaignResult) {
    return {
      success: false,
      errors: ["Error creating campaign"],
    };
  }
  return createCampaignResult;
}

/**
 * PREFILTERING JOBS
 */
export async function listCampaignPrefilteringJobs(
  jobName: string,
  pageSize: number = 10,
  options?: {
    NameContains?: string;
    LastEvaluatedKey?: AWS.DynamoDB.Key;
    NextToken?: string;
  }
): Promise<{
  campaignPrefilteringJobs: CampaignPrefilteringJob[];
  LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
  count: number;
}> {
  const dynamodb = new AWS.DynamoDB({
    endpoint: `dynamodb.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  return await new Promise<{
    campaignPrefilteringJobs: CampaignPrefilteringJob[];
    LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
    count: number;
  }>((resolve, reject) => {
    dynamodb.query(
      {
        ExclusiveStartKey: options?.LastEvaluatedKey,
        TableName: CAMPAIGN_PREFILTERING_TABLE,
        Limit: pageSize,
        ExpressionAttributeValues: {
          ":v1": {
            S: jobName,
          },
        },
        IndexName:
          "geo-gt-campaign-prefiltering-campaign-job-name-date-created-index",
        KeyConditionExpression: "CampaignJobName = :v1",
        ScanIndexForward: false,
      },
      (err, data) => {
        if (err) {
          reject(`Error fetching data: ${err.code}`);
          return;
        }
        if (!data.Items) {
          resolve({
            campaignPrefilteringJobs: [],
            LastEvaluatedKey: undefined,
            count: 0,
          });
          return;
        }

        const campaignPrefilteringJobs = data.Items.map(
          (item) =>
            AWS.DynamoDB.Converter.unmarshall(
              item
            ) as unknown as CampaignPrefilteringJob
        );
        resolve({
          campaignPrefilteringJobs,
          LastEvaluatedKey: data.LastEvaluatedKey,
          count: data.Count || 0,
        });
      }
    );
  });
}
export async function createCampaignPrefilteringJob(
  request: CreateCampaignPrefilteringJobRequest
): Promise<CreateCampaignPrefilteringJobResult> {
  const lambda = new AWS.Lambda({
    endpoint: `lambda.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  const result = await lambda
    .invoke({
      FunctionName: "initialize_prefiltering_job",
      Payload: JSON.stringify(request),
    })
    .promise();
  if (!result.Payload) {
    return {
      success: false,
      errors: ["No payload returned from lambda initialize_prefiltering_job"],
    };
  }
  const createCampaignPrefilteringJobResult = JSON.parse(
    result.Payload.toString()
  ) as CreateCampaignPrefilteringJobResult;
  if ("errorMessage" in createCampaignPrefilteringJobResult) {
    console.log(
      "createCampaignPrefilteringJobResult:",
      createCampaignPrefilteringJobResult
    );
    return {
      success: false,
      errors: ["Error creating campaign prefiltering job"],
    };
  }
  return createCampaignPrefilteringJobResult;
}

/**
 * CAMPAIGN GROUND TRUTH MANIFESTS
 */
export async function listCampaignManifests(
  campaignName: string,
  pageSize: number = 10,
  LastEvaluatedKey?: AWS.DynamoDB.Key
): Promise<{
  campaignManifests: CampaignManifest[];
  LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
  count: number;
}> {
  const dynamodb = new AWS.DynamoDB({
    endpoint: `dynamodb.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  return await new Promise<{
    campaignManifests: CampaignManifest[];
    LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
    count: number;
  }>((resolve, reject) => {
    dynamodb.query(
      {
        ExclusiveStartKey: LastEvaluatedKey,
        TableName: CAMPAIGN_MANIFEST_TABLE,
        Limit: pageSize,
        ExpressionAttributeValues: {
          ":v1": {
            S: campaignName,
          },
        },
        IndexName: "geo-gt-campaign-manifest-campaign-id-date-created-index",
        KeyConditionExpression: "CampaignJobName = :v1",
        ScanIndexForward: false,
      },
      (err, data) => {
        if (err) {
          reject(`Error fetching data: ${err.code}`);
          return;
        }
        if (!data.Items) {
          resolve({
            campaignManifests: [],
            LastEvaluatedKey: undefined,
            count: 0,
          });
          return;
        }

        const campaignManifests = data.Items.map(
          (item) =>
            AWS.DynamoDB.Converter.unmarshall(
              item
            ) as unknown as CampaignManifest
        );
        resolve({
          campaignManifests,
          LastEvaluatedKey: data.LastEvaluatedKey,
          count: data.Count || 0,
        });
      }
    );
  });
}

/** Create a ground truth input manifest file for this campaign */
export async function generateCampaignManifest(request: {
  campaignId: string;
  manifestType: ManifestType;
  campaignPrefilteringId?: string;
  minConfidenceThreshold?: number;
  numFramesPerTask?: number;
  limit?: number;
}): Promise<GenerateCampaignManifestResult> {
  const lambda = new AWS.Lambda({
    endpoint: `lambda.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  const result = await lambda
    .invoke({
      FunctionName: "create_campaign_manifest",
      Payload: JSON.stringify({
        campaign_id: request.campaignId,
        manifest_type: request.manifestType,
        campaign_prefiltering_id: request.campaignPrefilteringId,
        min_confidence_threshold: request.minConfidenceThreshold,
        num_frames_per_task: request.numFramesPerTask,
        limit: request.limit,
      }),
    })
    .promise();
  if (!result.Payload) {
    return {
      success: false,
      errors: ["No payload returned from lambda create_campaign_manifest"],
    };
  }
  const lambdaResult = JSON.parse(
    result.Payload.toString()
  ) as CreateCampaignManifestResult;
  if ("errorMessage" in lambdaResult) {
    console.log(lambdaResult.errorMessage);
    const errors = ["Error creating campaign manifest"];
    if (lambdaResult.errorMessage) {
      errors.push(lambdaResult.errorMessage);
    }
    return {
      success: false,
      errors,
    };
  }
  return lambdaResult;
}

/**
 * LABELING JOBS
 */
export async function listCampaignLabelingJobs(
  campaignJobName: string,
  pageSize: number = 10,
  LastEvaluatedKey?: AWS.DynamoDB.Key
): Promise<{
  campaignLabelingJobs: CampaignLabelingJob[];
  LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
  count: number;
}> {
  const dynamodb = new AWS.DynamoDB({
    endpoint: `dynamodb.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  return await new Promise<{
    campaignLabelingJobs: CampaignLabelingJob[];
    LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
    count: number;
  }>((resolve, reject) => {
    dynamodb.query(
      {
        ExclusiveStartKey: LastEvaluatedKey,
        TableName: CAMPAIGN_LABELING_JOB_TABLE,
        Limit: pageSize,
        ExpressionAttributeValues: {
          ":v1": {
            S: campaignJobName,
          },
        },
        IndexName:
          "geo-gt-campaign-labeling-job-campaign-job-name-date-created-index",
        KeyConditionExpression: "CampaignJobName = :v1",
        ScanIndexForward: false,
      },
      (err, data) => {
        if (err) {
          reject(`Error fetching data: ${err.code}`);
          return;
        }
        if (!data.Items) {
          resolve({
            campaignLabelingJobs: [],
            LastEvaluatedKey: undefined,
            count: 0,
          });
          return;
        }

        const campaignLabelingJobs = data.Items.map(
          (item) =>
            AWS.DynamoDB.Converter.unmarshall(
              item
            ) as unknown as CampaignLabelingJob
        );
        resolve({
          campaignLabelingJobs,
          LastEvaluatedKey: data.LastEvaluatedKey,
          count: data.Count || 0,
        });
      }
    );
  });
}

/**
 * PREFILTERING ENDPOINTS
 */
export async function listCampaignPrefilteringEndpoints(
  pageSize: number = 10,
  LastEvaluatedKey?: AWS.DynamoDB.Key
): Promise<{
  campaignPrefilteringEndpoints: CampaignPrefilteringEndpoint[];
  LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
  count: number;
}> {
  const dynamodb = new AWS.DynamoDB({
    endpoint: `dynamodb.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  return await new Promise<{
    campaignPrefilteringEndpoints: CampaignPrefilteringEndpoint[];
    LastEvaluatedKey: AWS.DynamoDB.Key | undefined;
    count: number;
  }>((resolve, reject) => {
    dynamodb.scan(
      {
        ExclusiveStartKey: LastEvaluatedKey,
        TableName: CAMPAIGN_PREFILTERING_ENDPOINT_TABLE,
        Limit: pageSize,
      },
      (err, data) => {
        if (err) {
          reject(`Error fetching data: ${err.code}`);
          return;
        }
        if (!data.Items) {
          resolve({
            campaignPrefilteringEndpoints: [],
            LastEvaluatedKey: undefined,
            count: 0,
          });
          return;
        }

        const campaignPrefilteringEndpoints = data.Items.map(
          (item) =>
            AWS.DynamoDB.Converter.unmarshall(
              item
            ) as unknown as CampaignPrefilteringEndpoint
        );
        resolve({
          campaignPrefilteringEndpoints,
          LastEvaluatedKey: data.LastEvaluatedKey,
          count: data.Count || 0,
        });
      }
    );
  });
}

/** CREATE/LINK CAMPAIGN LABELING JOBS */
export async function linkCampaignLabelingJob(request: {
  campaignName: string;
  groundTruthLabelingJobName: string;
  prefilteringEndpointName?: string;
  prefilteringConfidenceThreshold?: string;
}): Promise<LinkCampaignLabelingJobResult> {
  const lambda = new AWS.Lambda({
    endpoint: `lambda.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });
  const result = await lambda
    .invoke({
      FunctionName: "link_labeling_job_lambda",
      Payload: JSON.stringify({
        campaign_name: request.campaignName,
        labeling_job_name: request.groundTruthLabelingJobName,
      }),
    })
    .promise();
  if (!result.Payload) {
    return {
      success: false,
      errors: ["No payload returned from lambda link_labeling_job_lambda"],
    };
  }
  const lambdaResult = JSON.parse(
    result.Payload.toString()
  ) as LinkCampaignLabelingJobResult;
  if ("errorMessage" in lambdaResult) {
    console.log(lambdaResult.errorMessage);
    const errors = ["Error linking campaign labeling job"];
    if (lambdaResult.errorMessage) {
      errors.push(lambdaResult.errorMessage);
    }
    return {
      success: false,
      errors,
    };
  }
  return lambdaResult;
}

/** Ground truth labeling job actions */
export async function listGroundTruthLabelingJobs(options?: {
  NameContains?: string;
  NextToken?: string;
}): Promise<{
  groundTruthLabelingJobSummaries: LabelingJobSummary[];
  NextToken: string | undefined;
}> {
  if (!AWS.config.credentials) {
    return {
      groundTruthLabelingJobSummaries: [],
      NextToken: undefined,
    };
  }
  const sagemaker = new SageMaker({
    endpoint: `https://api.sagemaker.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
    region: appConfig.awsRegion,
    credentials: AWS.config.credentials,
  });

  const response = await sagemaker.listLabelingJobs({
    NextToken: options?.NextToken,
    NameContains: options?.NameContains,
  });

  return {
    groundTruthLabelingJobSummaries: response.LabelingJobSummaryList || [],
    NextToken: response.NextToken,
  };
}
